import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { day } from 'libs/day';
import {
  ACTIVITY_RESTRICTION_ERRORS_TITLES,
  CheckinMeta,
  RESTRICTION_ERRORS,
  RestrictionErrorMinimal,
} from 'libs/domain';
import { flex_time_activity_pb as ft_activity_pb } from 'libs/generated-grpc-web';
import { mingaSettingTypes } from 'libs/util';

import { BarcodeScanner } from '@app/src/app/barcodeScanner';
import { RootService } from '@app/src/app/minimal/services/RootService';
import { PermissionsService } from '@app/src/app/permissions';
import { MediaService } from '@app/src/app/shared/services/media';
import { MingaSettingsService } from '@app/src/app/store/Minga/services';

import { PS_CAMERA_NOT_FOUND_REOPEN_DELAY } from '@modules/people-selector/constants';
import { PsFilterState, PsSearchType, PsSummaryErrorsData, PsSearchMode, PsData } from '@modules/people-selector/types';

import {
  CloseTimeoutDuration,
  SystemAlertModalHangTime,
  SystemAlertModalService,
  SystemAlertModalType,
} from '@shared/components/system-alert-modal';
import { SystemAlertSnackBarService } from '@shared/components/system-alert-snackbar';
import { CheckinService } from '@shared/services/checkin';
import { FlexTimePermissionsService, FlexTimeRegistrationService } from '@shared/services/flex-time';
import { FlexTimeRegistrationValidationService } from '@shared/services/flex-time/flex-time-registration-validation.service';

import { PeopleSelectorService } from '../people-selector.service';
import { PeopleSelectorFormService } from '../ps-form.service';
import { PsCollectionSearchImplService } from '../search-impl/ps-collection-search.impl.service';
import { CacheService } from '@app/src/app/shared/services/cache/cache.service';
import { CacheKey } from '@app/src/app/shared/services/cache/cache.types';

@Injectable()
export class PsFlexTimeActivitiesService extends PeopleSelectorFormService<'FlexTime Activity'> {
  /** Service Constructor */
  constructor(
    public router: Router,
    public snackbar: SystemAlertSnackBarService,
    public barCodeScanner: BarcodeScanner,
    public peopleSelector: PeopleSelectorService,
    public permissions: PermissionsService,
    private _psCollectionSearch: PsCollectionSearchImplService,
    private _ftRegistration: FlexTimeRegistrationService,
    private _flexPermissions: FlexTimePermissionsService,
    private _systemAlertModal: SystemAlertModalService,
    private _checkinService: CheckinService,
    private _rootService: RootService,
    private _ftRegistrationValidation: FlexTimeRegistrationValidationService,
    private _mediaService: MediaService,
    private _mingaSettingsService: MingaSettingsService,
    private _cacheService: CacheService,
  ) {
    super(
      {
        name: 'FlexTime Activity',
        pageDefinitions: {
          assign: {
            submitFn: async =>
              this._ftRegistrationValidation.assign({
                activityId: this.data.flexTimeActivityId,
                periodId: this.data.flexTimePeriodId,
                selectedPeople: this.selection.getSelection(),
                canOverride: this.data.canOverride,
              }),
            searchFn: async => this._psCollectionSearch.collectionSearch('text', undefined),
          },
          register: {
            submitFn: async =>
              this._ftRegistrationValidation.register({
                activityId: this.data.flexTimeActivityId,
                periodId: this.data.flexTimePeriodId,
                selectedPeople: this.selection.getSelection(),
                canOverride: this.data.canOverride,
              }),
            searchFn: async => this._psCollectionSearch.collectionSearch('text', undefined),
          },
          remove: {
            submitFn: async => this.remove(),
            searchFn: async => this._fetchCurrentAssigned(),
          },
          checkin: {
            submitFn: async => this._checkin(),
            searchFn: async => this._fetchCurrentAssigned(true),
          },
        },
      },
      router,
      snackbar,
      barCodeScanner,
      peopleSelector,
    );

    this.setConfig({
      name: 'FlexTime Activity',
      pageDefinitions: {
        assign: {
          submitFn: async () => {
            const response = await this._ftRegistrationValidation.assign({
              activityId: this.data.flexTimeActivityId,
              periodId: this.data.flexTimePeriodId,
              selectedPeople: this.selection.getSelection(),
              canOverride: this.data.canOverride,
            });

            this._clearMyClassFlexListsCache();
            return response;
        },
          searchFn: async (type, filters) => this._enhancedSearch(type, filters),
        },
        register: {
          submitFn: async () =>
            {
              const response = await this._ftRegistrationValidation.register({
                activityId: this.data.flexTimeActivityId,
                periodId: this.data.flexTimePeriodId,
                selectedPeople: this.selection.getSelection(),
                canOverride: this.data.canOverride,
              });

              this._clearMyClassFlexListsCache();
              return response;
          },
          searchFn: async (type, filters) => this._enhancedSearch(type, filters),
        },
        remove: {
          submitFn: async () => this.remove(),
          searchFn: async () => this._fetchCurrentAssigned(),
        },
        checkin: {
          submitFn: async () => this._checkin(),
          searchFn: async () => this._fetchCurrentAssigned(true),
          secondaryActionFn: async () => this._checkin({ isTardy: true }),
          isSecondaryActionEnabledFn: async () => {
            return await this._mingaSettingsService.getSettingValue(mingaSettingTypes.FTM_ENABLE_TARDY_CHECK_IN);
          },
          text: {
            secondaryActionLabel: this._mediaService.isMobileView ? 'Tardy' : 'Check in tardy',
          },
        },
      },
    });
  }

  onInit(): void {
    this._disablePages();
  }

  public async remove(): Promise<void> {
    const selected = this.selection.getSelection();
    try {
      await this._ftRegistration.delete(
        this.data.flexTimeActivityId,
        selected.map(person => person.personHash),
      );
      const modalRef = await this._systemAlertModal.open({
        modalType: SystemAlertModalType.SUCCESS,
        heading: 'Registration Removed',
        message: 'Successfully removed registration for',
        subMessage: selected.map(person => person.displayName).join(', '),
        hangTime: SystemAlertModalHangTime.LONG,
      });
      await modalRef.afterClosed().toPromise();
      this._clearMyClassFlexListsCache();
    } catch (e) {
      this.snackBar.open({
        type: 'error',
        message: 'Error removing registration for user, please try again later',
      });
    }
  }

  private async _checkin(meta?: CheckinMeta): Promise<void> {
    const canOverrideRole = this._flexPermissions.isFlexTimeSelfManagerOrHigher();
    const personHashes = this.selection.getSelectionHashes();
    try {
      const { errors, successPeopleHashes } = await this._checkinService.createCheckin(
        personHashes,
        this.data.checkinReasonId,
        {
          note: this.data.note,
          meta,
        },
      );
      if (successPeopleHashes.length > 0 && errors.length === 0) {
        const hasManyPeople = personHashes.length > 1;
        const firstPerson = this.selection.getSelected(personHashes[0]);
        const isTardyCheckin = meta?.isTardy || false;

        await this.peopleSelector.openDialog({
          type: 'success',
          audioAlerts: true,
          title: hasManyPeople ? undefined : firstPerson.displayName,
          subTitle: hasManyPeople ? `${personHashes.length} people checked in` : 'Checked in',
          message: `${isTardyCheckin ? 'Tardy - ' : ''}${this.formTitle}`,
          closeTimeout: this.data?.showProfilePicture ? CloseTimeoutDuration.MEDIUM : CloseTimeoutDuration.SHORT,
          profilePicture: this.data?.showProfilePicture && firstPerson?.profilePicture,
        });
      } else if (errors.length > 0) {
        let reCheckInPeopleHashes: string[] = [];

        if (errors.length === 1) {
          const { personHash, code } = errors[0];
          const person = this.selection.getSelected(personHash);
          const title = person.displayName;
          const result = await this.peopleSelector.openDialog({
            type: 'error',
            audioAlerts: true,
            title,
            subTitle: RESTRICTION_ERRORS[code].title || 'Check in denied',
            message: this.data.flexTimeActivityName,
            canContinue: canOverrideRole,
            extendedMessage:
              RESTRICTION_ERRORS[code].message || 'Some unknown error has occured while trying to process your request',
          });

          if (result?.action === 'continue') {
            reCheckInPeopleHashes.push(personHash);
          }
        } else {
          const errorsData = this._makeErrorData(successPeopleHashes, errors);

          const hashes = await this.peopleSelector.openSummary({
            message: 'could not be checked in',
            title: 'Check in summary',
            subMessage: 'Select the people you want to “Check in”',
            submitLabel: 'Check in',
            errorsData,
          });

          reCheckInPeopleHashes = [...hashes];
        }

        if (reCheckInPeopleHashes.length > 0) {
          const hasManyPeople = reCheckInPeopleHashes.length > 1;
          const firstPerson = this.selection.getSelected(reCheckInPeopleHashes[0]);
          await this._checkinService.createCheckin(reCheckInPeopleHashes, this.data.checkinReasonId, {
            note: this.data.note,
            meta: { forceCheckIn: true, ...meta },
          });
          await this.peopleSelector.openDialog({
            type: 'success',
            audioAlerts: true,
            title: hasManyPeople ? undefined : firstPerson?.displayName || '',
            subTitle: 'Checked in',
            message: this.formTitle,
            closeTimeout: this.data?.showProfilePicture ? CloseTimeoutDuration.MEDIUM : CloseTimeoutDuration.SHORT,
            profilePicture: this.data?.showProfilePicture && firstPerson?.profilePicture,
          });
        }
      }
    } catch (error) {
      // TODO this should be a service
      let offline = true;
      try {
        await this._fetchCurrentAssigned(true); // this can be any request pretty much
        offline = false;
      } catch (e) {
        //swallow this
        console.error('offline', e);
      }

      let modalRef;

      if (offline) {
        //offline error
        modalRef = await this._systemAlertModal.open({
          modalType: SystemAlertModalType.ERROR,
          icon: 'mg-offline',
          heading: 'Offline',
          message: "We couldn't check in the user because there is no internet connection",
        });
      } else {
        //generic error
        modalRef = await this._systemAlertModal.open({
          modalType: SystemAlertModalType.ERROR,
          heading: 'Check in error',
          message: 'Something went wrong, please try again',
        });
      }

      //wait for the modal to close
      if (modalRef) await modalRef.afterClosed().toPromise();
    }
  }

  public async onListScanNoResults(id: string, searchMode: PsSearchMode): Promise<void> {
    const page = this.pages.getActivePath();
    if (page === 'checkin') await this._searchPersonAndCheckin(id, searchMode);
  }

  private async _fetchCurrentAssigned(showCheckInStatus = false): Promise<PsData[]> {
    const results = await this._ftRegistration.fetchRegisteredPeople(this.data.flexTimeActivityId, {
      checkinReasonId: this.data.checkinReasonId,
    });
    return results.map(person => ({
      personHash: person.personHash,
      displayName: person.displayName,
      grade: person.grade,
      studentId: person.studentId,
      status: showCheckInStatus ? person.checkInStatus : person.registeredStatus,
      profilePicture: person.filename,
    }));
  }

  private async _searchPersonAndCheckin(id: string, searchMode: PsSearchMode): Promise<void> {
    try {
      let hardStop = false;
      const { flexTimeActivityId, flexTimePeriodId } = this.data;
      const canOveride = this._flexPermissions.isFlexTimeAdmin();

      const searchResults = await this._psCollectionSearch.collectionSearch('text', {
        ...this.filters.getCurrentState(),
        keywords: id,
      });

      if (!searchResults || searchResults.length === 0) {
        throw Error(`No person found for ID: ${id}`);
      }

      const { displayName, personHash } = searchResults[0];

      const { existingRegistrations, restrictionsErrors } = await this._rootService.addLoadingPromise(
        this._ftRegistration.validate(flexTimePeriodId, [personHash], flexTimeActivityId),
      );

      // wait for user to acknowledge restriction errors
      if (restrictionsErrors && restrictionsErrors.length > 0 && !hardStop) {
        for (const restriction of restrictionsErrors) {
          if (hardStop) break;
          const { code } = restriction;
          const dialogRef = await this.peopleSelector.openDialog({
            type: 'error',
            audioAlerts: true,
            title: displayName,
            message: this.formTitle,
            subTitle: ACTIVITY_RESTRICTION_ERRORS_TITLES[code].header,
            extendedMessage: RESTRICTION_ERRORS[code].message || 'Would you like to register and check the student in?',
            canContinue: true,
            closeText: 'Close',
            continueText: 'Check in',
          });
          hardStop = dialogRef.action !== 'continue';
        }
      }

      // wait for user to acknowledge registration errors
      if (existingRegistrations && existingRegistrations.length > 0 && !hardStop) {
        const { activityName, activityTeacherName, registeredBy, activityLocation, type, studentName } =
          existingRegistrations[0];
        const isAssigment = type === ft_activity_pb.RegistrationType.ASSIGNMENT;
        const action = isAssigment ? 'assign' : 'register';
        const dialogRef = await this.peopleSelector.openDialog({
          type: 'error',
          audioAlerts: true,
          title: displayName,
          message: this.formTitle,
          subTitle: `${isAssigment ? 'Assigned' : 'Registered'} to another activity`,
          extendedMessage: canOveride
            ? `Would you like to ${action} and check the student in?`
            : isAssigment
            ? `A student can't be checked in if they are assigned to another activity`
            : 'Would you like to register and check the student in?',
          detailedMessage: [
            [isAssigment ? 'Assigned by' : 'Registered by', registeredBy],
            ['Student', studentName],
            ['Activity', activityName],
            ['Teacher', activityTeacherName],
            ['Location', activityLocation],
          ],
          canContinue: canOveride || !isAssigment,
          closeText: canOveride || !isAssigment ? 'Deny' : 'Close',
          continueText: (canOveride || !isAssigment) && 'Check in',
        });
        hardStop = dialogRef.action !== 'continue';
      }
      // wait for user to acknowledge student is not registered for anything
      else {
        const dialogRef = await this.peopleSelector.openDialog({
          type: 'error',
          audioAlerts: true,
          title: displayName,
          message: this.formTitle,
          subTitle: 'Not registered to this activity',
          extendedMessage: 'Would you like to register and check the student in?',
          canContinue: true,
          closeText: 'Close',
          continueText: 'Check in',
        });
        hardStop = dialogRef.action !== 'continue';
      }

      if (hardStop) throw new Error('Cancelled check in');

      await this._ftRegistration.assign(flexTimeActivityId, [personHash], false, true);

      await this._checkinService.createCheckin([personHash], this.data.checkinReasonId);

      await new Promise(resolve => setTimeout(resolve, 100));

      await this.search();

      await this.peopleSelector.openDialog({
        type: 'success',
        audioAlerts: true,
        title: displayName,
        subTitle: 'Checked in',
        message: this.formTitle,
        closeTimeout: this.data?.showProfilePicture ? CloseTimeoutDuration.MEDIUM : CloseTimeoutDuration.SHORT,
        profilePicture: this.data?.showProfilePicture && searchResults[0]?.profilePicture,
      });

      if (searchMode === 'camera') this.peopleSelector.emitEvent('open-camera');
    } catch (error) {
      const snackRef = this.snackBar.open({
        type: 'error',
        message: error?.message || 'Something went wrong, please try again later',
      });
      if (searchMode === 'camera') {
        await new Promise(resolve => setTimeout(resolve, PS_CAMERA_NOT_FOUND_REOPEN_DELAY));
        snackRef.dismiss();
        this.peopleSelector.emitEvent('open-camera');
      }
    }
  }

  private _disablePages() {
    if (this.data.canOverride === false) {
      const endDateTime = new Date(this.data.date);
      const [hours, minutes, seconds] = this.data.endTime.split(':').map(Number);
      endDateTime.setHours(hours, minutes, seconds);

      const isRegistrationDeadlinePast = day(this.data.registrationDeadline).isBefore(day());
      const isPeriodEnded = endDateTime < new Date();

      if (
        isRegistrationDeadlinePast ||
        isPeriodEnded ||
        this.data.isblockedFromManageRegistration
      ) {
        this.pages.addDisabledPage(['assign', 'register', 'remove']);
        this.pages.setActive('checkin');
      }
    }
  }

  protected _makeErrorData(
    successPersonHashes: string[],
    errorPersonHashes?: RestrictionErrorMinimal[],
  ): PsSummaryErrorsData[] {
    const success = successPersonHashes.map(hash => {
      const person = this.selection.getSelected(hash);
      return {
        hash: person.personHash,
        name: person.displayName,
        status: 'success',
        reasons: ['Checked in'],
      } as PsSummaryErrorsData;
    });
    const errors = errorPersonHashes.map(({ personHash, code }) => {
      const person = this.selection.getSelected(personHash);
      return {
        hash: person.personHash,
        name: person.displayName,
        status: 'error',
        reasons: [RESTRICTION_ERRORS[code].title],
      } as PsSummaryErrorsData;
    });
    return [...success, ...errors];
  }

  /**
   * this search function works just like the normal mongo people search, but it also fetches the
   * currently registered people for the flextime activity and adds their registered status
   * (e.g. registered, assigned, etc.) to the search results
   */
  private async _enhancedSearch(type: PsSearchType, filters: PsFilterState): Promise<PsData[]> {
    const [peopleCollection, registeredPeople] = await Promise.all([
      this._psCollectionSearch.collectionSearch(type, filters),
      this._ftRegistration.fetchRegisteredPeople(this.data.flexTimeActivityId, {
        checkinReasonId: this.data.checkinReasonId,
      }),
    ]);
    const statusMap = new Map<string, string>();
    for (const person of registeredPeople) {
      statusMap.set(person.personHash, person.registeredStatus);
    }

    return peopleCollection.map(person => ({
      ...person,
      status: statusMap.get(person.personHash) || '-',
    }));
  }

  private _clearMyClassFlexListsCache() {
    this._cacheService.clearCacheByNamespace(CacheKey.MY_CLASS_FLEX_LIST_MEMBERS);
    this._cacheService.clearCacheByNamespace(CacheKey.MY_CLASS_PERIOD_REGISTRATIONS);
  }
}
